import { type Node, type NodeProps, Position } from '@xyflow/react';
import React from 'react';

import { BaseNode, type NodeData } from './BaseNode';
import { NodeHandle } from './NodeHandle';

// Helper function to generate type descriptions
const getTypeDescription = (value: unknown, maxLength: number = 50): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const type = typeof value;

  if (type === 'string') return 'string';
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'function') return 'function';
  if (type === 'symbol') return 'symbol';
  if (type === 'bigint') return 'bigint';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'list()';

    // Get the type of the first element to infer array type
    const firstElementType = getTypeDescription(value[0], 20);
    return `list(${firstElementType})`;
  }

  if (type === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) return 'object({})';

    // Build object type description
    const obj = value as Record<string, unknown>;
    const keyDescriptions: string[] = [];
    let currentLength = 8; // Start with 'object()'

    for (const key of keys) {
      const keyType = getTypeDescription(obj[key], 15);
      const keyDesc = `"${key}": ${keyType}`;

      // Check if adding this key would exceed max length
      if (currentLength + keyDesc.length + 2 > maxLength) {
        // +2 for ', '
        keyDescriptions.push('...');
        break;
      }

      keyDescriptions.push(keyDesc);
      currentLength += keyDesc.length + 2;
    }

    const content = keyDescriptions.join(', ');
    return `object({${content}})`;
  }

  return type;
};

export type ConstantNodeType = Node<NodeData, 'custom_constant'>;

export const ConstantNode: React.FC<NodeProps<ConstantNodeType>> = ({
  id,
  data,
  selected,
}) => {
  const value = data.config?.config_value;

  let subtitle: string | undefined;
  if (value !== undefined) {
    const typeDescription = getTypeDescription(value);
    subtitle = typeDescription;
  }

  // Extract connection state from node data for consistency (source-only node)
  const activeConnection = data.activeConnection;
  const isActiveConnection = !!activeConnection;

  return (
    <BaseNode
      nodeId={id}
      data={data}
      category="io"
      subtitle={subtitle}
      selected={selected}
    >
      {/* Output handle for constant blocks */}
      <NodeHandle
        type="source"
        position={Position.Right}
        id="value"
        blockId={id}
        isActiveConnection={isActiveConnection}
      />
    </BaseNode>
  );
};
